import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Grid2, Paper, Box, Button, TextField,
  Card, CardContent, IconButton, List, ListItem, ListItemText,
  ListItemIcon, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Chip, Stack, Alert, Snackbar, FormControl, InputLabel,
  Select, MenuItem
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlaceIcon from '@mui/icons-material/Place';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import SaveIcon from '@mui/icons-material/Save';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SensorsIcon from '@mui/icons-material/Sensors';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CloseIcon from '@mui/icons-material/Close';

// ===== 颜色方案 =====
const MARKER_COLORS = [
  '#f44336', '#e91e63', '#9c27b0', '#2196f3',
  '#00bcd4', '#4caf50', '#ff9800', '#ff5722',
];

function getMarkerColor(index) {
  return MARKER_COLORS[index % MARKER_COLORS.length];
}

// ===== 本地存储 key =====
const STORAGE_KEY = 'sensor_location_data';

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* ignore */ }
  return null;
}

function saveToStorage(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

// ===== Layout 管理（多 layout 支持）=====
const LAYOUTS_KEY = 'sensor_location_layouts';

function loadLayouts() {
  try {
    const raw = localStorage.getItem(LAYOUTS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* ignore */ }
  return [];
}

function saveLayouts(layouts) {
  localStorage.setItem(LAYOUTS_KEY, JSON.stringify(layouts));
}

// ===== 主组件 =====
function SensorLocation({ userData }) {
  const navigate = useNavigate();

  // Layout 管理
  const [layouts, setLayouts] = useState(() => loadLayouts());
  const [currentLayoutId, setCurrentLayoutId] = useState(null);

  // 画布相关
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [layoutImage, setLayoutImage] = useState(null); // Image object
  const [layoutImageSrc, setLayoutImageSrc] = useState(null); // base64 src
  const [layoutName, setLayoutName] = useState('');

  // 标注（markers）
  const [markers, setMarkers] = useState([]);
  const [isPlacing, setIsPlacing] = useState(false); // 正在放置标注模式
  const [selectedMarker, setSelectedMarker] = useState(null);

  // 缩放 & 平移
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });

  // 弹窗
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', type: 'sensor', sensorId: '' });
  const [pendingPosition, setPendingPosition] = useState(null); // 等待命名的新标注位置

  // 提示消息
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // 可用传感器列表（从 API 加载）
  const [availableSensors, setAvailableSensors] = useState([]);

  // 实时传感器数据（用于 popup 显示）
  const [allSensorData, setAllSensorData] = useState({});

  // 标注弹窗（点击地图上的 dot 时显示）
  const [markerPopup, setMarkerPopup] = useState(null);

  // 新建 layout 弹窗
  const [newLayoutDialog, setNewLayoutDialog] = useState(false);
  const [newLayoutName, setNewLayoutName] = useState('');

  // ===== 加载可用传感器（dropdown + 实时数据）=====
  useEffect(() => {
    const fetchSensors = async () => {
      try {
        const res = await fetch('/api/sensor/lastrecord');
        if (res.ok) {
          const data = await res.json();
          if (data && !data.error) {
            setAllSensorData(data);
            setAvailableSensors(
              Object.entries(data).map(([k, v]) => ({ id: k, name: v.name || k }))
                .sort((a, b) => a.name.localeCompare(b.name))
            );
          }
        }
      } catch (e) {
        console.log('Could not load sensors');
      }
    };
    fetchSensors();
    const iv = setInterval(async () => {
      try {
        const res = await fetch('/api/sensor/lastrecord');
        if (res.ok) {
          const d = await res.json();
          if (d && !d.error) setAllSensorData(d);
        }
      } catch(e) {}
    }, 30000);
    return () => clearInterval(iv);
  }, []);

  // ===== 当前 layout 切换时从 layouts 加载数据 =====
  useEffect(() => {
    if (currentLayoutId !== null) {
      const layout = layouts.find(l => l.id === currentLayoutId);
      if (layout) {
        setLayoutImageSrc(layout.imageSrc);
        setLayoutName(layout.name);
        setMarkers(layout.markers || []);
        setScale(1);
        setOffset({ x: 0, y: 0 });
        // 加载图片对象
        const img = new Image();
        img.onload = () => setLayoutImage(img);
        img.src = layout.imageSrc;
      }
    }
  }, [currentLayoutId, layouts]);

  // ===== 绘制 Canvas =====
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layoutImage) return;

    const ctx = canvas.getContext('2d');
    const container = containerRef.current;
    if (!container) return;

    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();

    // 应用缩放和平移
    ctx.translate(offset.x, offset.y);
    ctx.scale(scale, scale);

    // 计算图片适配尺寸（fit contain）
    const imgRatio = layoutImage.width / layoutImage.height;
    const canvasRatio = canvas.width / canvas.height;
    let drawW, drawH, drawX, drawY;
    if (imgRatio > canvasRatio) {
      drawW = canvas.width / scale;
      drawH = drawW / imgRatio;
      drawX = 0;
      drawY = (canvas.height / scale - drawH) / 2;
    } else {
      drawH = canvas.height / scale;
      drawW = drawH * imgRatio;
      drawX = (canvas.width / scale - drawW) / 2;
      drawY = 0;
    }

    // 绘制底图
    ctx.drawImage(layoutImage, drawX, drawY, drawW, drawH);

    // 存储绘制范围供点击计算
    canvas._drawRect = { drawX, drawY, drawW, drawH };

    // 绘制标注
    markers.forEach((marker, index) => {
      const mx = drawX + marker.x * drawW;
      const my = drawY + marker.y * drawH;
      const isSelected = selectedMarker === index;
      const color = getMarkerColor(index);

      // 标注图钉
      ctx.save();
      ctx.translate(mx, my);

      // 阴影
      ctx.shadowColor = 'rgba(0,0,0,0.4)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 2;

      // 圆点
      const radius = isSelected ? 10 : 7;
      ctx.beginPath();
      ctx.arc(0, 0, radius, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = isSelected ? '#fff' : 'rgba(255,255,255,0.8)';
      ctx.lineWidth = isSelected ? 3 : 2;
      ctx.stroke();

      // 标签
      ctx.shadowColor = 'transparent';
      const label = marker.name || `Sensor ${index + 1}`;
      ctx.font = `${isSelected ? 'bold ' : ''}12px Arial`;
      const textWidth = ctx.measureText(label).width;

      // 标签背景
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      const padding = 4;
      ctx.fillRect(
        -textWidth / 2 - padding,
        -radius - 20,
        textWidth + padding * 2,
        16
      );

      // 标签文字
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, 0, -radius - 12);

      ctx.restore();
    });

    ctx.restore();
  }, [layoutImage, markers, scale, offset, selectedMarker]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // 窗口 resize 重绘
  useEffect(() => {
    const handleResize = () => drawCanvas();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawCanvas]);

  // ===== 文件上传处理 =====
  const handleFileUpload = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      showSnackbar('请上传图片文件 (PNG/JPG/SVG)', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showSnackbar('文件大小不能超过 10MB', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target.result;
      setLayoutImageSrc(src);
      const img = new Image();
      img.onload = () => {
        setLayoutImage(img);
        setScale(1);
        setOffset({ x: 0, y: 0 });
        setMarkers([]);
        showSnackbar('Layout 上传成功！');
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  };

  // ===== 拖拽上传 =====
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // ===== 根据 marker 查找实时数据 =====
  const getMarkerSensorData = useCallback((marker) => {
    if (!marker) return null;
    const searchKey = marker.sensorId || marker.name;
    const entry = Object.entries(allSensorData).find(
      ([k, v]) => k === searchKey || v.name === searchKey
    );
    if (!entry) return null;
    const [deviceId, sd] = entry;
    try {
      const scd = sd.SCD ? JSON.parse(sd.SCD) : null;
      const sps = sd.SPS ? JSON.parse(sd.SPS) : null;
      return {
        deviceId,
        co2:  scd?.['3'] ?? null,
        temp: scd?.['4'] != null ? scd['4'] / 100 : null,
        rh:   scd?.['5'] != null ? scd['5'] / 100 : null,
        pm25: sps?.['1'] != null ? sps['1'] / 100 : null,
      };
    } catch(e) { return null; }
  }, [allSensorData]);

  // ===== Canvas 点击：放置新标注 or 选中已有标注 =====
  const handleCanvasClick = (e) => {
    if (!layoutImage) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const drawRect = canvas._drawRect;
    if (!drawRect) return;

    const canvasX = (e.clientX - rect.left - offset.x) / scale;
    const canvasY = (e.clientY - rect.top - offset.y) / scale;

    if (isPlacing) {
      const relX = (canvasX - drawRect.drawX) / drawRect.drawW;
      const relY = (canvasY - drawRect.drawY) / drawRect.drawH;
      if (relX < 0 || relX > 1 || relY < 0 || relY > 1) return;
      setPendingPosition({ x: relX, y: relY });
      setEditForm({ name: '', description: '', type: 'sensor', sensorId: '' });
      setEditDialogOpen(true);
      setIsPlacing(false);
      return;
    }

    const HIT_R = 14;
    setMarkerPopup(null);
    for (let i = markers.length - 1; i >= 0; i--) {
      const m = markers[i];
      const mx = drawRect.drawX + m.x * drawRect.drawW;
      const my = drawRect.drawY + m.y * drawRect.drawH;
      if (Math.sqrt((canvasX - mx) ** 2 + (canvasY - my) ** 2) < HIT_R) {
        const containerRect = containerRef.current.getBoundingClientRect();
        setMarkerPopup({
          markerIndex: i,
          x: e.clientX - containerRect.left,
          y: e.clientY - containerRect.top,
        });
        setSelectedMarker(i);
        return;
      }
    }
    setSelectedMarker(null);
  };

  // ===== 鼠标拖拽平移 =====
  const handleMouseDown = (e) => {
    if (isPlacing) return;
    setMarkerPopup(null);
    setIsPanning(true);
    panStart.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      setOffset({
        x: e.clientX - panStart.current.x,
        y: e.clientY - panStart.current.y,
      });
      return;
    }
    if (!isPlacing && layoutImage && canvasRef.current?._drawRect) {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const dr = canvas._drawRect;
      const cx = (e.clientX - rect.left - offset.x) / scale;
      const cy = (e.clientY - rect.top - offset.y) / scale;
      const onMarker = markers.some(m => {
        const mx = dr.drawX + m.x * dr.drawW;
        const my = dr.drawY + m.y * dr.drawH;
        return Math.sqrt((cx - mx) ** 2 + (cy - my) ** 2) < 14;
      });
      canvas.style.cursor = onMarker ? 'pointer' : 'grab';
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // ===== 缩放（滚轮）=====
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.max(0.2, Math.min(5, prev * delta)));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('wheel', handleWheel, { passive: false });
      return () => canvas.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  // ===== 保存新标注 =====
  const handleSaveMarker = () => {
    if (pendingPosition) {
      const newMarker = {
        ...pendingPosition,
        name: editForm.name || `Sensor ${markers.length + 1}`,
        description: editForm.description,
        type: editForm.type,
        sensorId: editForm.sensorId || editForm.name,
        createdAt: new Date().toISOString(),
      };
      const updated = [...markers, newMarker];
      setMarkers(updated);
      autoSaveLayout(updated);
      showSnackbar(`已添加标注: ${newMarker.name}`);
    }
    setEditDialogOpen(false);
    setPendingPosition(null);
  };

  // ===== 编辑已有标注 =====
  const handleEditMarker = (index) => {
    const m = markers[index];
    setEditForm({ name: m.name, description: m.description || '', type: m.type || 'sensor', sensorId: m.sensorId || m.name });
    setSelectedMarker(index);
    setPendingPosition(null);
    setEditDialogOpen(true);
  };

  const handleUpdateMarker = () => {
    if (selectedMarker !== null && !pendingPosition) {
      const updated = [...markers];
      updated[selectedMarker] = {
        ...updated[selectedMarker],
        name: editForm.name,
        description: editForm.description,
        type: editForm.type,
        sensorId: editForm.sensorId || editForm.name,
      };
      setMarkers(updated);
      autoSaveLayout(updated);
      showSnackbar(`已更新: ${editForm.name}`);
    }
    setEditDialogOpen(false);
    setSelectedMarker(null);
  };

  // ===== 删除标注 =====
  const handleDeleteMarker = (index) => {
    const name = markers[index]?.name;
    const updated = markers.filter((_, i) => i !== index);
    setMarkers(updated);
    autoSaveLayout(updated);
    if (selectedMarker === index) setSelectedMarker(null);
    showSnackbar(`已删除: ${name}`, 'warning');
  };

  // ===== 自动保存到 layout =====
  const autoSaveLayout = (updatedMarkers) => {
    if (currentLayoutId === null) return;
    const updatedLayouts = layouts.map(l =>
      l.id === currentLayoutId
        ? { ...l, markers: updatedMarkers || markers }
        : l
    );
    setLayouts(updatedLayouts);
    saveLayouts(updatedLayouts);
  };

  // ===== 新建 Layout =====
  const handleCreateLayout = () => {
    if (!layoutImageSrc || !newLayoutName.trim()) return;
    const newLayout = {
      id: Date.now(),
      name: newLayoutName.trim(),
      imageSrc: layoutImageSrc,
      markers: [],
      createdAt: new Date().toISOString(),
    };
    const updated = [...layouts, newLayout];
    setLayouts(updated);
    saveLayouts(updated);
    setCurrentLayoutId(newLayout.id);
    setMarkers([]);
    setNewLayoutDialog(false);
    setNewLayoutName('');
    showSnackbar(`Layout "${newLayout.name}" 已创建`);
  };

  // ===== 删除 Layout =====
  const handleDeleteLayout = (id) => {
    const updated = layouts.filter(l => l.id !== id);
    setLayouts(updated);
    saveLayouts(updated);
    if (currentLayoutId === id) {
      setCurrentLayoutId(null);
      setLayoutImage(null);
      setLayoutImageSrc(null);
      setMarkers([]);
    }
    showSnackbar('Layout 已删除', 'warning');
  };

  // ===== 导出数据 =====
  const handleExport = () => {
    const data = { layouts };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sensor_locations.json';
    a.click();
    URL.revokeObjectURL(url);
    showSnackbar('数据已导出');
  };

  // ===== 导入数据 =====
  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data.layouts && Array.isArray(data.layouts)) {
          setLayouts(data.layouts);
          saveLayouts(data.layouts);
          showSnackbar('数据已导入');
        }
      } catch (err) {
        showSnackbar('导入失败：无效的 JSON 文件', 'error');
      }
    };
    reader.readAsText(file);
  };

  // ===== 加载 HKU Demo =====
  const handleLoadHKUDemo = () => {
    const demoMarkers = [
      { x: 0.35, y: 0.42, name: 'Haking Wong Bldg', description: '黄克競楼 - 温湿度传感器', type: 'sensor' },
      { x: 0.27, y: 0.50, name: 'Knowles Bldg', description: '知行楼 - 室内空气质量', type: 'sensor' },
      { x: 0.30, y: 0.37, name: 'Run Run Shaw Bldg', description: '邵逸夫楼 - 温度传感器', type: 'sensor' },
      { x: 0.45, y: 0.57, name: 'Main Building', description: '本部大楼 - 能耗监测', type: 'sensor' },
      { x: 0.55, y: 0.39, name: 'Composite Bldg', description: '综合大楼 - CO2 传感器', type: 'sensor' },
      { x: 0.73, y: 0.38, name: 'Centennial Garden', description: '百周年花园 - 户外气象站', type: 'sensor' },
      { x: 0.34, y: 0.30, name: 'Library', description: '图书馆 - 人流计数器', type: 'sensor' },
      { x: 0.20, y: 0.45, name: 'Chong Yuet Ming Physics', description: '庄月明物理楼 - 实验室环境', type: 'sensor' },
    ];

    // 使用 public 目录下的 HKU 地图
    const img = new Image();
    img.onload = () => {
      // 将图片转为 base64 以便存储
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const base64 = canvas.toDataURL('image/png');

      const demoLayout = {
        id: Date.now(),
        name: 'HKU Campus Map (Demo)',
        imageSrc: base64,
        markers: demoMarkers,
        createdAt: new Date().toISOString(),
      };
      const updated = [...layouts, demoLayout];
      setLayouts(updated);
      saveLayouts(updated);
      setCurrentLayoutId(demoLayout.id);
      showSnackbar('HKU Demo 已加载！可点击地图添加更多标注');
    };
    img.onerror = () => {
      showSnackbar('未找到 HKU 地图文件，请将图片保存为 public/hku-campus-map.png', 'error');
    };
    img.src = process.env.PUBLIC_URL + '/hku-campus-map.png';
  };

  // ===== 缩放控制 =====
  const handleZoomIn = () => setScale(prev => Math.min(5, prev * 1.2));
  const handleZoomOut = () => setScale(prev => Math.max(0.2, prev * 0.8));
  const handleResetView = () => { setScale(1); setOffset({ x: 0, y: 0 }); };

  // ===== Snackbar =====
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // ===== 容器样式 =====
  const containerStyle = {
    maxHeight: window.innerWidth < 500 ? '70vh' : '77vh',
    overflowY: 'auto',
    padding: '10px',
  };

  // ===== Layout 选择器（左侧面板）=====
  const renderLayoutPanel = () => (
    <Card sx={{ mb: 2, bgcolor: 'background.paper' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <MyLocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Layout 管理
        </Typography>

        {/* Layout 列表 */}
        <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
          {layouts.map((layout) => (
            <ListItem
              key={layout.id}
              secondaryAction={
                <IconButton edge="end" size="small" onClick={() => handleDeleteLayout(layout.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              }
              sx={{
                bgcolor: currentLayoutId === layout.id ? 'rgba(33,150,243,0.15)' : 'transparent',
                borderRadius: 1, mb: 0.5, cursor: 'pointer',
              }}
              onClick={() => setCurrentLayoutId(layout.id)}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <PlaceIcon fontSize="small" color={currentLayoutId === layout.id ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText
                primary={layout.name}
                secondary={`${(layout.markers || []).length} 个标注`}
              />
            </ListItem>
          ))}
          {layouts.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              暂无 Layout，请上传图片新建
            </Typography>
          )}
        </List>

        {/* 上传按钮 */}
        <Box sx={{ mt: 2 }}>
          <input
            type="file"
            accept="image/*"
            id="layout-upload"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleFileUpload(file);
                setNewLayoutName(file.name.replace(/\.[^.]+$/, ''));
                setNewLayoutDialog(true);
              }
            }}
          />
          <label htmlFor="layout-upload">
            <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} fullWidth>
              上传新 Layout
            </Button>
          </label>
        </Box>

        {/* HKU Demo */}
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          startIcon={<PlaceIcon />}
          onClick={handleLoadHKUDemo}
          fullWidth
          sx={{ mt: 1 }}
        >
          加载 HKU Demo
        </Button>

        {/* 导入/导出 */}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <input type="file" accept=".json" id="import-data" hidden onChange={handleImport} />
          <label htmlFor="import-data" style={{ flex: 1 }}>
            <Button variant="text" component="span" size="small" startIcon={<UploadFileIcon />} fullWidth>
              导入
            </Button>
          </label>
          <Button variant="text" size="small" startIcon={<DownloadIcon />} onClick={handleExport} sx={{ flex: 1 }}>
            导出
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  // ===== 标注列表面板 =====
  const renderMarkerPanel = () => (
    <Card sx={{ bgcolor: 'background.paper' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6">
            <SensorsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            传感器标注
          </Typography>
          <Chip label={`${markers.length} 个`} size="small" />
        </Stack>

        {layoutImage && (
          <Button
            variant={isPlacing ? 'contained' : 'outlined'}
            color={isPlacing ? 'warning' : 'primary'}
            startIcon={<AddLocationIcon />}
            onClick={() => setIsPlacing(!isPlacing)}
            fullWidth
            sx={{ mb: 1 }}
          >
            {isPlacing ? '点击地图放置标注...' : '添加标注'}
          </Button>
        )}

        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
          {markers.map((marker, index) => (
            <ListItem
              key={index}
              secondaryAction={
                <Stack direction="row" spacing={0}>
                  <IconButton size="small" onClick={() => handleEditMarker(index)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteMarker(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
              sx={{
                bgcolor: selectedMarker === index ? 'rgba(33,150,243,0.15)' : 'transparent',
                borderRadius: 1, mb: 0.5, cursor: 'pointer',
              }}
              onClick={() => setSelectedMarker(selectedMarker === index ? null : index)}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Box sx={{
                  width: 12, height: 12, borderRadius: '50%',
                  bgcolor: getMarkerColor(index), border: '2px solid white'
                }} />
              </ListItemIcon>
              <ListItemText
                primary={marker.name}
                secondary={marker.description || `(${(marker.x * 100).toFixed(1)}%, ${(marker.y * 100).toFixed(1)}%)`}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  // ===== 渲染 =====
  return (
    <Container maxWidth="xl" style={containerStyle}>
      <Grid2 container spacing={2}>
        {/* 左侧面板 */}
        <Grid2 size={{ xs: 12, md: 3 }}>
          {renderLayoutPanel()}
          {currentLayoutId && renderMarkerPanel()}
        </Grid2>

        {/* 右侧画布 */}
        <Grid2 size={{ xs: 12, md: 9 }}>
          <Card sx={{ bgcolor: 'background.paper', height: '72vh' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1 }}>
              {/* 工具栏 */}
              {layoutImage && (
                <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                  <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                    {layoutName || 'Layout'}
                  </Typography>
                  <Tooltip title="缩小"><IconButton size="small" onClick={handleZoomOut}><ZoomOutIcon /></IconButton></Tooltip>
                  <Typography variant="body2">{(scale * 100).toFixed(0)}%</Typography>
                  <Tooltip title="放大"><IconButton size="small" onClick={handleZoomIn}><ZoomInIcon /></IconButton></Tooltip>
                  <Tooltip title="重置视图"><IconButton size="small" onClick={handleResetView}><RestartAltIcon /></IconButton></Tooltip>
                </Stack>
              )}

              {/* Canvas 区域 */}
              <Box
                ref={containerRef}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                sx={{
                  flex: 1,
                  position: 'relative',
                  border: isPlacing ? '2px dashed #ff9800' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: isPlacing ? 'crosshair' : (isPanning ? 'grabbing' : 'grab'),
                  bgcolor: '#2a2a2a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {layoutImage ? (
                  <canvas
                    ref={canvasRef}
                    onClick={handleCanvasClick}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : (
                  <Box
                    sx={{ textAlign: 'center', color: 'text.secondary', p: 4 }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <CloudUploadIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6">拖拽图片到此处</Typography>
                    <Typography variant="body2">或从左侧上传 2D Layout 图片</Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      支持 PNG / JPG / SVG，最大 10MB
                    </Typography>
                  </Box>
                )}

                {/* 放置模式提示 */}
                {isPlacing && (
                  <Alert
                    severity="info"
                    sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)' }}
                  >
                    点击地图上任意位置放置传感器标注
                  </Alert>
                )}

                {/* ===== 传感器数据气泡 ===== */}
                {markerPopup !== null && (() => {
                  const marker = markers[markerPopup.markerIndex];
                  const sd = getMarkerSensorData(marker);
                  const color = getMarkerColor(markerPopup.markerIndex);
                  const containerW = containerRef.current?.clientWidth || 600;
                  const containerH = containerRef.current?.clientHeight || 400;
                  const POPUP_W = 210;
                  const POPUP_H = 200;
                  const popX = Math.min(markerPopup.x + 14, containerW - POPUP_W - 4);
                  const popY = Math.max(Math.min(markerPopup.y - 60, containerH - POPUP_H - 4), 4);
                  return (
                    <Box
                      sx={{
                        position: 'absolute', left: popX, top: popY,
                        width: POPUP_W, bgcolor: 'rgba(24,24,24,0.97)',
                        border: `1.5px solid ${color}`, borderRadius: 2,
                        p: 1.5, zIndex: 200, boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
                        pointerEvents: 'auto',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        size="small"
                        onClick={() => setMarkerPopup(null)}
                        sx={{ position: 'absolute', top: 2, right: 2, color: '#666', p: 0.4 }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>

                      <Typography sx={{ color, fontWeight: 700, fontSize: 13, pr: 2, mb: 0.3 }}>
                        {marker?.name}
                      </Typography>
                      {marker?.description && (
                        <Typography sx={{ color: '#888', fontSize: 11, mb: 0.8 }}>
                          {marker.description}
                        </Typography>
                      )}

                      {sd ? (
                        <Box sx={{ mb: 1 }}>
                          {sd.co2  != null && <Typography sx={{ fontSize: 12, color: sd.co2 > 1000 ? '#f44336' : '#81C784' }}>CO2: {sd.co2} ppm</Typography>}
                          {sd.temp != null && <Typography sx={{ fontSize: 12, color: '#FFAB91' }}>Temp: {sd.temp.toFixed(1)} °C</Typography>}
                          {sd.rh   != null && <Typography sx={{ fontSize: 12, color: '#80DEEA' }}>RH: {sd.rh.toFixed(0)} %</Typography>}
                          {sd.pm25 != null && <Typography sx={{ fontSize: 12, color: '#FFCC02' }}>PM2.5: {sd.pm25.toFixed(1)} ug/m3</Typography>}
                        </Box>
                      ) : (
                        <Typography sx={{ color: '#555', fontSize: 11, mb: 1 }}>
                          {(marker?.sensorId && marker.sensorId !== marker.name) ? 'No live data' : 'Not linked to sensor'}
                        </Typography>
                      )}

                      {sd?.deviceId && (
                        <Button
                          size="small" variant="outlined" fullWidth
                          onClick={() => {
                            navigate(`/ieq?device=${encodeURIComponent(sd.deviceId)}`);
                            setMarkerPopup(null);
                          }}
                          sx={{ fontSize: 11, py: 0.3, borderColor: color, color, '&:hover': { borderColor: color, bgcolor: `${color}22` } }}
                        >
                          View IEQ Details
                        </Button>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      {/* ===== 编辑/新建标注弹窗 ===== */}
      <Dialog open={editDialogOpen} onClose={() => { setEditDialogOpen(false); setPendingPosition(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>{pendingPosition ? '添加传感器标注' : '编辑标注'}</DialogTitle>
        <DialogContent>
          {availableSensors.length > 0 ? (
            <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
              <InputLabel>关联传感器</InputLabel>
              <Select
                value={editForm.sensorId || editForm.name}
                label="关联传感器"
                onChange={(e) => {
                  const sel = availableSensors.find(s => s.id === e.target.value);
                  setEditForm({ ...editForm, sensorId: e.target.value, name: editForm.name || (sel?.name ?? e.target.value) });
                }}
              >
                <MenuItem value=""><em>不关联</em></MenuItem>
                {availableSensors.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <TextField
            autoFocus
            label="名称"
            fullWidth
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            sx={{ mb: 2, mt: availableSensors.length > 0 ? 0 : 1 }}
            placeholder="例如：温度传感器 A1"
          />
          <TextField
            label="描述（可选）"
            fullWidth
            multiline
            rows={2}
            value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            sx={{ mb: 2 }}
            placeholder="例如：位于三楼走廊"
          />
          <FormControl fullWidth>
            <InputLabel>类型</InputLabel>
            <Select
              value={editForm.type}
              label="类型"
              onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
            >
              <MenuItem value="sensor">传感器</MenuItem>
              <MenuItem value="gateway">网关</MenuItem>
              <MenuItem value="camera">摄像头</MenuItem>
              <MenuItem value="other">其他</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setPendingPosition(null); }}>取消</Button>
          <Button
            variant="contained"
            onClick={pendingPosition ? handleSaveMarker : handleUpdateMarker}
            startIcon={<SaveIcon />}
          >
            {pendingPosition ? '添加' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== 新建 Layout 弹窗 ===== */}
      <Dialog open={newLayoutDialog} onClose={() => setNewLayoutDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>新建 Layout</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Layout 名称"
            fullWidth
            value={newLayoutName}
            onChange={(e) => setNewLayoutName(e.target.value)}
            sx={{ mt: 1 }}
            placeholder="例如：HKU Main Campus"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewLayoutDialog(false)}>取消</Button>
          <Button variant="contained" onClick={handleCreateLayout} startIcon={<SaveIcon />}>
            创建
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Snackbar ===== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default SensorLocation;
